import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { flatten } from "lodash";
import { getFlags } from "../../../../state/selectors/CardsUI.selectors";
import CardWrapper from "../../CardWrapper";
import Card from "../../Card";
import tailwindColors from "../../../../../../tailwind.colors";
import { getPathogenicityColorByAllNames } from "../../../../common/helpers/Constant";
import { getPubmedEntriesPerArticle } from "../../../../state/selectors/PubmedUserEntries.selectors";
import {
  getSearchEntity,
  getCommentsCount,
} from "../../../../state/selectors/Publications.selectors";
import SourceEntity from "../../../Publications/models/SourceEntity";
import {
  benignColor,
  likelyBenignColor,
  likelyPathogenicColor,
  pathogenicColor,
  uncertainSignificanceColor,
} from "../../../../common/helpers/colors";
import { HorizontalStackedBar } from "../../../atoms";
import threadService from "../../../../services/threadService";

const pathoShortToPatho = {
  B: "Benign",
  LB: "Benign",
  LP: "Pathogenic",
  P: "Pathogenic",
  VUS: "Uncertain Significance",
};

const pathoToColor = {
  Pathogenic: pathogenicColor,
  "Likely Pathogenic": likelyPathogenicColor,
  "Uncertain Significance": uncertainSignificanceColor,
  "Likely Benign": likelyBenignColor,
  Benign: benignColor,
  Other: " #CD519D",
};

const flagValues = ["P", "LP", "VUS", "LB", "B"];
const sortingOrder = Object.keys(pathoToColor);

const CommunityContributionsVariantCard = ({
  card,
  flags,
  linkedPublications,
  sourceEntity,
  commentsCount,
}) => {
  useEffect(() => {
    threadService
      .loadTopicAndComments(sourceEntity.type, sourceEntity.id)
      .subscribe();
  }, [sourceEntity.type, sourceEntity.id]);

  const calcBackgroundColor = () => {
    let color = tailwindColors.blueGray[400];
    if (flags.length > 0) {
      for (const flagValue of flagValues) {
        const result = flags.filter(
          (classification) => classification?.flag === flagValue
        );
        if (result.length > 0) {
          color = getPathogenicityColorByAllNames(flagValue);
          break;
        }
      }
    }
    return color;
  };

  if (__IS_COMPONENTS_PORTAL__) {
    return (
      <CardWrapper {...card} color={calcBackgroundColor()}>
        <>
          {flags?.length || commentsCount ? (
            <>
              <span className="tw-font-bold" data-testid="classifications">
                <span className="tw-font-normal tw-pr-1">Classifications:</span>
                {flags?.length || 0}
              </span>
              <div>
                <span className="tw-font-bold">
                  <span data-testid="comments">
                    <span className="tw-font-normal tw-pr-1">Comments:</span>
                    {commentsCount}
                  </span>
                </span>
              </div>
            </>
          ) : null}
        </>
      </CardWrapper>
    );
  }

  const userClassifications = useMemo(() => {
    if (!linkedPublications || !linkedPublications.size) return null;
    const res = flatten(Array.from(linkedPublications.values()))
      .map(({ pathogenicity }) => pathoShortToPatho[pathogenicity] ?? "Other")
      .reduce(
        (prev, cur) => ({
          ...prev,
          [cur]: prev[cur] ? prev[cur] + 1 : 1,
        }),
        {}
      );
    return [...Object.entries(res)]
      .map(([key, value]) => {
        return {
          value,
          title: key,
          text: `${value}`,
          color: pathoToColor[key],
          renderTooltipElement: (
            <div>
              <span>
                This variant was classified as{" "}
                <span
                  className="tw-font-bold"
                  style={{ color: pathoToColor[key] }}
                >
                  {key}
                </span>{" "}
                {value} {value > 1 ? "times" : "time"} while linking a
                publication
              </span>
            </div>
          ),
        };
      })
      .sort(
        (a, b) => sortingOrder.indexOf(a.title) - sortingOrder.indexOf(b.title)
      );
  }, [linkedPublications]);

  return (
    <CardWrapper {...card} color={calcBackgroundColor()}>
      <>
        {flags?.length || !!linkedPublications.size ? (
          <>
            <span className="tw-font-bold" data-testid="classifications">
              <span className="tw-font-normal tw-pr-1">Classifications:</span>
              {flags?.length || 0}
            </span>
            <span className="tw-font-bold">
              <span data-testid="total-linked-publications">
                <span className="tw-font-normal tw-pr-1">,</span>
                <span className="tw-font-normal tw-pr-1">Publications:</span>
                {linkedPublications.size}
              </span>
            </span>
          </>
        ) : null}
        {!!linkedPublications.size && (
          <span data-testid="user-linked-pubs-pathogenicity">
            <HorizontalStackedBar
              className="tw-h-5 tw-text-xs tw-min-w-stackedBar tw-text-white"
              data={userClassifications}
              forceDisabledToolTip={true}
            />
          </span>
        )}
      </>
    </CardWrapper>
  );
};

CommunityContributionsVariantCard.propTypes = {
  card: PropTypes.instanceOf(Card).isRequired,
  flags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      user__screen_name: PropTypes.string,
      flag: PropTypes.string,
      comment: PropTypes.string,
      json_data: PropTypes.shape({}),
      flagged_at: PropTypes.string,
      user__full_name: PropTypes.string,
      user__open_id_identifier_hashed: PropTypes.string,
    })
  ),
  linkedPublications: PropTypes.instanceOf(Map),
  sourceEntity: PropTypes.instanceOf(SourceEntity),
  commentsCount: PropTypes.number,
};

CommunityContributionsVariantCard.defaultProps = {
  flags: [],
  linkedPublications: new Map(),
  sourceEntity: null,
  commentsCount: 0,
};

const mapStateToProps = (state) => {
  return {
    sourceEntity: getSearchEntity(state),
    flags: getFlags(state),
    linkedPublications: getPubmedEntriesPerArticle(state),
    commentsCount: getCommentsCount(state),
  };
};

export default connect(mapStateToProps)(CommunityContributionsVariantCard);
